/**
 * @sw-package b2b
 */

type BootstrapToast = {
    show: () => void,
};

type BootstrapToastConstructor = {
    getOrCreateInstance?: (element: HTMLElement, config?: Record<string, unknown>) => BootstrapToast,
    new (element: HTMLElement, config?: Record<string, unknown>): BootstrapToast,
};

type BootstrapGlobal = {
    Toast?: BootstrapToastConstructor,
};

type BootstrapToastVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

type BootstrapToastOptions = {
    delay?: number,
    variant?: BootstrapToastVariant,
};

declare const bootstrap: BootstrapGlobal | undefined;

const TOAST_CONTAINER_ID = 'b2b-quote-bootstrap-toast-container';
const DEFAULT_TOAST_DELAY = 8000;

export function showBootstrapToast(message: unknown, options: BootstrapToastOptions = {}): void {
    const normalizedMessage = normalizeToastMessage(message);

    if (!normalizedMessage) {
        return;
    }

    const toastElement = createToastElement(normalizedMessage, options.variant || 'danger');
    const delay = options.delay || DEFAULT_TOAST_DELAY;

    getToastContainer().append(toastElement);

    if (typeof bootstrap === 'undefined' || !bootstrap.Toast) {
        toastElement.classList.add('show');
        toastElement.querySelector('[data-bs-dismiss="toast"]')?.addEventListener('click', () => {
            toastElement.remove();
        }, { once: true });
        window.setTimeout(() => toastElement.remove(), delay);

        return;
    }

    const toast = bootstrap.Toast.getOrCreateInstance?.(toastElement, { delay })
        || new bootstrap.Toast(toastElement, { delay });

    toastElement.addEventListener('hidden.bs.toast', () => {
        toastElement.remove();
    }, { once: true });

    toast.show();
}

function getToastContainer(): HTMLElement {
    const existingContainer = document.getElementById(TOAST_CONTAINER_ID);

    if (existingContainer) {
        return existingContainer;
    }

    const container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.className = 'toast-container position-fixed top-0 end-0 p-3';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'true');

    document.body.append(container);

    return container;
}

function createToastElement(message: string, variant: BootstrapToastVariant): HTMLElement {
    const toast = document.createElement('div');
    toast.className = `toast align-items-center text-bg-${variant} border-0`;
    toast.setAttribute('role', variant === 'danger' ? 'alert' : 'status');
    toast.setAttribute('aria-live', variant === 'danger' ? 'assertive' : 'polite');
    toast.setAttribute('aria-atomic', 'true');

    const content = document.createElement('div');
    content.className = 'd-flex';

    const body = document.createElement('div');
    body.className = 'toast-body';
    body.textContent = message;

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = shouldUseWhiteCloseButton(variant)
        ? 'btn-close btn-close-white me-2 m-auto'
        : 'btn-close me-2 m-auto';
    closeButton.setAttribute('data-bs-dismiss', 'toast');
    closeButton.setAttribute('aria-label', 'Close');

    content.append(body, closeButton);
    toast.append(content);

    return toast;
}

function shouldUseWhiteCloseButton(variant: BootstrapToastVariant): boolean {
    return !['warning', 'light'].includes(variant);
}

function normalizeToastMessage(message: unknown): string {
    if (message instanceof Error) {
        return message.message.trim();
    }

    if (typeof message === 'string') {
        return message.trim();
    }

    return '';
}
